import { useEffect, useMemo, useState } from 'react';
import { adoptSession } from '../lib/api';
import { asLang, setLang, useT } from '../lib/i18n';
import { getInitData, haptic, openExternal, tg } from '../lib/telegram';

/**
 * Coach sign-up (src/coach/signup.ts): one screen, then «Первые шаги».
 * Opened from the bot's «Открыть регистрацию» (t.me/<bot>?start=coach).
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error ?? `http_${res.status}`), { contact: data.contact });
  return data as T;
}

const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
  н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};
/** As on the server (signup.ts, slugFrom): «Ренат Иванов» → renat-ivanov. */
const slugFrom = (name: string) => [...name.toLowerCase()].map((ch) => TRANSLIT[ch] ?? ch).join('')
  .normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 31).replace(/-+$/, '');

type State = 'loading' | 'new' | 'closed' | 'limit' | 'error';
type SlugState = 'ok' | 'bad_format' | 'reserved' | 'taken' | 'checking';

export function CoachSignup({ onAlreadyCoach }: { onAlreadyCoach: () => void }) {
  const t = useT();
  const [state, setState] = useState<State>('loading');
  const [contact, setContact] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [lang, setLangChoice] = useState<'ru' | 'en'>(asLang(tg?.initDataUnsafe.user?.language_code));
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Nicosia');
  const [slug, setSlug] = useState('');
  // «Что вы тренируете?»: sets the coach's sports (tennis brings its skills, coach/sports.ts).
  const [sport, setSport] = useState<'padel' | 'tennis' | 'both'>('padel');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugState, setSlugState] = useState<SlugState>('checking');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const zones = useMemo(() => {
    try { return (Intl as any).supportedValuesOf('timeZone') as string[]; } catch { return [] as string[]; }
  }, []);

  useEffect(() => { setLang(lang); }, [lang]);

  useEffect(() => {
    post<{ state: State | 'coach'; contact: string; prefill: { name: string; language: 'ru' | 'en'; slug: string } }>(
      '/api/signup/state', { initData: getInitData() })
      .then((r) => {
        if (r.state === 'coach') { onAlreadyCoach(); return; }
        setContact(r.contact);
        setName(r.prefill.name);
        setSlug(r.prefill.slug);
        setLangChoice(r.prefill.language);
        setState(r.state);
      })
      .catch(() => setState('error'));
  }, []);

  // The link follows the name until the coach edits it.
  useEffect(() => { if (!slugTouched) setSlug(slugFrom(name)); }, [name, slugTouched]);

  useEffect(() => {
    if (!slug) { setSlugState('bad_format'); return; }
    setSlugState('checking');
    const h = setTimeout(() => {
      fetch(`${BASE}/api/signup/slug?slug=${encodeURIComponent(slug)}`).then((r) => r.json())
        .then((r) => setSlugState(r.state)).catch(() => setSlugState('ok'));
    }, 300);
    return () => clearTimeout(h);
  }, [slug]);

  async function start() {
    setError(null);
    setBusy(true);
    try {
      const r = await post<{ token: string }>('/api/signup', {
        initData: getInitData(), name, city, language: lang, timezone, slug, sport,
      });
      haptic.success();
      adoptSession(r.token);
      // Into the coach app, with «Первые шаги» open.
      window.location.replace(`${window.location.pathname}?steps=1`);
    } catch (err: any) {
      haptic.error();
      const code = err.message as string;
      if (code === 'coach') { onAlreadyCoach(); return; }
      if (code === 'closed' || code === 'limit') { setContact(err.contact ?? contact); setState(code); return; }
      if (code === 'taken' || code === 'reserved' || code === 'bad_format') { setSlugState(code); setError(null); }
      else setError(code === 'bad_name' ? t('su.errName') : code === 'bad_city' ? t('su.errCity')
        : code === 'bad_timezone' ? t('su.errTimezone')
        : code === 'init_data_reused' || code === 'invalid_init_data' ? t('err.reopenApp') : t('co.failed', { message: code }));
    } finally { setBusy(false); }
  }

  if (state === 'loading') return <div className="app"><div className="list" style={{ paddingTop: 24 }}><div className="skeleton" /></div></div>;
  if (state === 'error') return <div className="app"><div className="empty"><div className="empty__lead">{t('app.signInFailed')}</div>{t('err.reopenApp')}</div></div>;
  if (state === 'closed' || state === 'limit') {
    return (
      <div className="app">
        <div className="empty su-contact">
          <div className="empty__lead">{t('su.contactTitle')}</div>
          {t(state === 'closed' ? 'su.contactClosed' : 'su.contactLimit')}
          {contact && (
            <button className="savebtn" style={{ marginTop: 14 }} onClick={() => openExternal(`https://t.me/${contact}`)}>@{contact}</button>
          )}
        </div>
      </div>
    );
  }

  const ready = name.trim() && city.trim() && timezone && slugState === 'ok' && !busy;
  const slugNote = slugState === 'checking' ? t('su.slugChecking') : slugState === 'ok' ? t('su.slugOk')
    : slugState === 'taken' ? t('su.slugTaken') : slugState === 'reserved' ? t('su.slugReserved') : t('su.slugFormat');

  return (
    <div className="app su">
      <div className="su__head">
        <h1 className="su__title">{t('su.title')}</h1>
        <p className="card__when">{t('su.lead')}</p>
      </div>
      <div className="list">
        <div className="card su__form">
          <label className="mf"><span className="mf__label">{t('su.name')}</span>
            <input className="notearea su__input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} /></label>
          <label className="mf"><span className="mf__label">{t('su.city')}</span>
            <input className="notearea su__input" value={city} maxLength={80} placeholder={t('su.cityPh')} onChange={(e) => setCity(e.target.value)} /></label>
          <div className="mf"><span className="mf__label">{t('su.language')}</span>
            <div className="repeat__opts">
              <button className="chip" aria-pressed={lang === 'ru'} onClick={() => setLangChoice('ru')}>Русский</button>
              <button className="chip" aria-pressed={lang === 'en'} onClick={() => setLangChoice('en')}>English</button>
            </div></div>
          <div className="mf"><span className="mf__label">{t('su.sport')}</span>
            <div className="repeat__opts">
              {(['padel', 'tennis', 'both'] as const).map((x) => (
                <button key={x} className="chip" aria-pressed={sport === x} onClick={() => setSport(x)}>
                  {t(x === 'padel' ? 'co.sport.padel' : x === 'tennis' ? 'co.sport.tennis' : 'co.sport.both')}
                </button>
              ))}
            </div></div>
          <label className="mf"><span className="mf__label">{t('su.timezone')}</span>
            <input className="notearea su__input" list="su-zones" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
            <datalist id="su-zones">{zones.map((z) => <option key={z} value={z} />)}</datalist></label>
          <label className="mf"><span className="mf__label">{t('su.slug')}</span>
            <span className="su__slug"><span className="su__slugpre">t.me/…?start=</span>
              <input className="notearea su__input" value={slug} maxLength={31} autoCapitalize="off" autoCorrect="off"
                onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-')); }} /></span>
            <span className={`su__slugnote su__slugnote--${slugState}`}>{slugNote}</span></label>
          <div className="su__trial">{t('su.trial')}</div>
          {error && <div className="warnline">{error}</div>}
          <button className="savebtn" disabled={!ready} onClick={start}>{busy ? t('su.starting') : t('su.start')}</button>
        </div>
      </div>
    </div>
  );
}

